import { BrandLockup } from '@/components/Logo'
import { istTime } from '@/lib/fmt'
import { cn } from '@/lib/utils'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { accountStore } from '@/store/account'
import { feedAgeOf, marketSessionText } from '@/screens/terminal/feedState'
import { tickSocket } from '@/feed/tickSocket'
import { useMarketContext } from '@/feed/useMarketContext'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * The wall clock, in IST.
 *
 * Always IST and labelled as such, whatever the browser's timezone: every
 * time a trader reasons about — the open, the close, an expiry, a timestamp
 * on a fill — is in exchange time, and a terminal showing the machine's local
 * clock beside those is an invitation to mis-read one.
 */
function Clock() {
  const [now, setNow] = useState(() => istTime())
  useEffect(() => {
    const t = setInterval(() => setNow(istTime()), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <span className="num text-[11px] text-muted-foreground" title="Asia/Kolkata">
      {now} IST
    </span>
  )
}

/**
 * Paper or Live, from the account actually selected on the Terminal.
 *
 * Was hardcoded to PAPER, which was honest while no order path existed and
 * is now the one thing on this bar that must never be wrong.
 */
function ModeBadge() {
  const market = useMarketContext()
  useSyncExternalStore(accountStore.subscribe, accountStore.getSnapshot, accountStore.getSnapshot)
  const mode = market.source === 'SIMULATION' ? 'PAPER' : accountStore.mode()
  return (
    <span className={cn('tr-badge', mode === 'LIVE' ? 'live' : 'paper')} data-testid="mode-badge">
      {market.source === 'SIMULATION' ? 'Simulation' : mode === 'LIVE' ? 'Live' : 'Paper'}
    </span>
  )
}

/**
 * Which broker account orders go to.
 *
 * Up here beside the market pill rather than in the chain's header: it is
 * chosen once a session and applies to everything, and moving it out is
 * what lets the chain's own controls fit on one row and start the ladder
 * higher.
 */
function AccountSelect() {
  const { connections, selectedId } = useSyncExternalStore(
    accountStore.subscribe,
    accountStore.getSnapshot,
    accountStore.getSnapshot,
  )
  if (connections.length === 0) return null
  const live = accountStore.mode() === 'LIVE'
  return (
    <span className="relative flex items-center">
      <span
        className={cn('tr-dot absolute left-[11px] z-10', live ? 'text-destructive' : 'up')}
        style={{ width: 7, height: 7 }}
      />
      <Select value={selectedId ?? ''} onValueChange={(v) => accountStore.select(v || null)}>
        <SelectTrigger
          aria-label="Trading account"
          data-testid="account-select"
          className="w-[190px] pl-[26px]"
        >
          <SelectValue placeholder="Select an account…" />
        </SelectTrigger>
        <SelectContent>
          {connections.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.label} · {c.mode}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </span>
  )
}

/**
 * How stale the prices are, in seconds.
 *
 * Beside the market pill and deliberately separate from it: the session is a
 * fact about the clock, this is a fact about our socket, and conflating them
 * is how "market closed" gets blamed for a dead feed.
 *
 * ITS OWN TIMER, NOT A STORE SUBSCRIPTION. `useFeedDiagnostics` wakes on
 * every frame, which at feed rate would re-render the top bar a dozen times a
 * second to move one decimal — the exact thing §4.7 exists to prevent. The
 * socket's counters are read on a 500ms poll instead, which is twice the
 * resolution the readout shows and costs nothing.
 */
const FEED_POLL_MS = 500

function FeedAgeChip() {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), FEED_POLL_MS)
    return () => window.clearInterval(t)
  }, [])

  const stats = tickSocket.getStats()
  const age = feedAgeOf({ lastFrameAt: stats.lastFrameAt, now, simulationActive: stats.marketSource === 'SIMULATION' })
  return (
    <span className="tr-pill tr-hide-md" title={age.detail} data-testid="feed-age">
      <span
        className={cn(
          'tr-dot',
          age.ok === true && 'up',
          age.ok === false && 'text-destructive',
          age.ok === null && 'text-muted-foreground',
        )}
      />
      <span className="num">FEED {age.text}</span>
    </span>
  )
}

/**
 * Whether the EXCHANGE is open. Not whether our feed is up.
 *
 * Deliberately separate from feed health, which is the chip beside it and the
 * chain's offline banner: the session is a fact about the clock, ours is a
 * fact about us, and conflating them is how "market closed" gets blamed for
 * a dead socket.
 */
function MarketPill() {
  const market = useMarketContext()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    // A minute is plenty: this only crosses at 09:15 and 16:00.
    const t = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(t)
  }, [])
  const session = marketSessionText(now, market.source === 'SIMULATION')
  return (
    <span className="tr-pill tr-hide-md" data-testid="market-pill">
      <span className={cn('tr-dot', session.open && 'up')} />
      {session.text}
    </span>
  )
}

/**
 * The bar carries only what changes WITHIN a session: the mode, how stale the
 * feed is, whether the exchange is open, which account an order goes to, and
 * the clock. Who is signed in, the theme and everything configured once now
 * live under the avatar at the foot of the rail — see `NavUser`.
 */
export function TopBar() {
  return (
    <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border bg-background px-3">
      {/*
        THE LOCKUP LIVES HERE, and only here.

        This bar spans the whole app and its left edge is the first thing
        read on every screen, so it is where the brand belongs. The rail
        beside it carries the icon and a text label instead — one canonical
        lockup per screen, because a second one stops reading as a logo and
        starts reading as a repeated image.

        22px against a 44px bar: tall enough for the wordmark to be legible,
        short enough that the bar's own content — mode, feed, account, clock
        — stays the thing being scanned.
      */}
      <BrandLockup height={22} />

      {/*
        PAPER is the only mode Phase 1 can be in. Rendering it as a hard badge
        rather than a subtle label is the point: trading-journal's mode gate is
        structural, and the UI should make the current mode impossible to
        misread before any order path exists.
      */}
      <ModeBadge />

      <div className="flex-1" />

      <FeedAgeChip />
      <MarketPill />
      <AccountSelect />
      <Clock />

    </header>
  )
}
